import { AuthenticatedAPIClient, type APIResponse } from "./authenticated-api";

export interface StudentImportRowError {
	row: number;
	message: string;
}

/** One column whose file value differs from what is currently in the DB. */
export interface StudentImportChangedColumn {
	dbColumn: string;
	xlsxHeader: string;
	oldValue: string | null;
	newValue: string | null;
}

/** A row whose `MaSV` already exists in the DB — needs an explicit skip/update decision. */
export interface StudentImportConflictRow {
	MaSV: string;
	row: number;
	hoTenInDb: string | null;
	hoTenInFile: string | null;
	changedColumns: StudentImportChangedColumn[];
}

export interface StudentImportPreview {
	totalRows: number;
	validRows: number;
	errors: StudentImportRowError[];
	duplicateMaSV: string[];
	/** New `MaSV` rows — inserted unconditionally, no decision needed. */
	insertCount: number;
	/** Existing `MaSV` rows that differ — each needs an explicit skip/update decision. */
	conflicts: StudentImportConflictRow[];
	/** Existing `MaSV` rows the file would not change — tự bỏ qua, không hỏi. */
	unchangedCount: number;
	/** Mã trường THPT cần duyệt tên trước khi ghi vào danh mục `high_school`. */
	highSchoolNameConflicts: HighSchoolNameConflict[];
}

/** `MaSV` -> decision. A `MaSV` absent from the map is always skipped. */
export type StudentImportDecision = "skip" | "update";
export type StudentImportDecisions = Record<string, StudentImportDecision>;

/** Một cách viết tên cho cùng một mã trường, kèm số sinh viên mang cách viết đó. */
export interface HighSchoolNameOption {
	name: string;
	students: number;
}

/** Một mã trường THPT cần admin chọn tên trước khi ghi vào danh mục. */
export interface HighSchoolNameConflict {
	system: string;
	code: string;
	/** Tên đang có trong danh mục, `null` nếu mã hoàn toàn mới. */
	currentName: string | null;
	/** Sắp giảm dần theo số sinh viên. */
	options: HighSchoolNameOption[];
}

/**
 * `"{system}:{code}"` -> tên được chọn. Giá trị hợp lệ là một `option` của
 * chính mã đó, hoặc `currentName` của nó (quyết định "giữ nguyên danh mục" —
 * hợp lệ kể cả khi không file nào đề xuất tên đó). Khoá vắng mặt, hoặc giá trị
 * ngoài hai loại trên, rơi về mặc định ở backend (ứng viên nhiều sinh viên nhất).
 */
export type HighSchoolNameDecisions = Record<string, string>;

export interface StudentImportCommitResult {
	inserted: number;
	updated: number;
	skipped: number;
}

export type StudentColumnType = "string" | "int" | "float" | "date" | "boolean";

export interface StudentColumnMapping {
	dbColumn: string;
	xlsxHeader: string | null;
	type: StudentColumnType;
	description: string;
}

/**
 * Unwraps the `{ success, message, data }` envelope, throwing `Error(message)`
 * on failure so callers have one catch shape. Mirrors `catalog-api.service.ts`.
 */
async function unwrap<T>(response: Promise<APIResponse<T>>): Promise<T> {
	const result = await response;
	if (!result.success || result.data === undefined) {
		throw new Error(result.message || "Yêu cầu thất bại");
	}
	return result.data;
}

/** Reads a `File` as a base64 string (no data: URL prefix). */
function fileToBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(reader.error ?? new Error("Không đọc được file"));
		reader.onload = () => {
			const result = reader.result as string;
			const base64 = result.slice(result.indexOf(",") + 1);
			resolve(base64);
		};
		reader.readAsDataURL(file);
	});
}

/**
 * Thin typed client for `/v1/student-import`. Uploads travel as base64 JSON
 * rather than multipart — the backend intentionally carries no `multer`
 * dependency for this single admin-only endpoint.
 */
class StudentImportApiService {
	private readonly baseUrl = "/student-import";

	async preview(file: File): Promise<StudentImportPreview> {
		const fileBase64 = await fileToBase64(file);
		return unwrap(
			AuthenticatedAPIClient.post<StudentImportPreview>(`${this.baseUrl}/preview`, {
				fileName: file.name,
				fileBase64,
			}),
		);
	}

	/**
	 * `decisions` governs every row whose `MaSV` already exists: a `MaSV`
	 * absent from the map is always skipped, never updated — the backend
	 * enforces this unconditionally, this client just carries the map through.
	 *
	 * `highSchoolNameDecisions` governs which name gets written for each mã
	 * trường in `highSchoolNameConflicts` — same "backend enforces, client just
	 * carries it through" contract.
	 */
	async commit(
		file: File,
		decisions: StudentImportDecisions,
		highSchoolNameDecisions: HighSchoolNameDecisions = {},
	): Promise<StudentImportCommitResult> {
		const fileBase64 = await fileToBase64(file);
		return unwrap(
			AuthenticatedAPIClient.post<StudentImportCommitResult>(`${this.baseUrl}/commit`, {
				fileName: file.name,
				fileBase64,
				decisions,
				highSchoolNameDecisions,
			}),
		);
	}

	listColumns(): Promise<StudentColumnMapping[]> {
		return unwrap(AuthenticatedAPIClient.get<StudentColumnMapping[]>(`${this.baseUrl}/columns`));
	}
}

export const studentImportApi = new StudentImportApiService();
